import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Drawer,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ClickAwayListener,
  LinearProgress,
} from "@mui/material";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useUploadFiles } from "@better-upload/client";
import axios from "axios";
import { baseUrl, uploadApiUrl } from "../config";

type InternalGif = {
  id: string;
  url: string;
  title: string;
  tags: string[];
};

type InternalGifsListResponse = {
  success: boolean;
  data: {
    items: InternalGif[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
};

type InternalGifTagsResponse = {
  success: boolean;
  data: {
    tags: string[];
  };
};

const TAG_CACHE_KEY = "internal-gif-tags-cache-v1";
const TAG_CACHE_TTL_MS = 1000 * 60 * 60 * 24; // 24 hours

type TagCachePayload = {
  tags: string[];
  savedAt: number;
};

export const Gifs = () => {
  const PAGE_SIZE = 20;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [gifs, setGifs] = useState<InternalGif[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isGridLoading, setIsGridLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploadedObjectKey, setUploadedObjectKey] = useState("");
  const [newGifName, setNewGifName] = useState("");
  const [newGifTags, setNewGifTags] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const readTagCache = (): TagCachePayload | null => {
    try {
      const raw = localStorage.getItem(TAG_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as TagCachePayload;
      if (!Array.isArray(parsed.tags) || typeof parsed.savedAt !== "number") {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const writeTagCache = (tags: string[]) => {
    try {
      const payload: TagCachePayload = { tags, savedAt: Date.now() };
      localStorage.setItem(TAG_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // no-op: cache is best effort
    }
  };

  const fetchGifs = useCallback(
    async (targetPage = page) => {
      setIsGridLoading(true);
      try {
        const response = await axios.get<InternalGifsListResponse>(
          `${baseUrl}/internal-gifs`,
          {
            params: {
              page: targetPage,
              limit: PAGE_SIZE,
              title: searchQuery.trim() || undefined,
              tags: selectedTags.length ? selectedTags.join(",") : undefined,
            },
          }
        );
        setGifs(response.data.data.items ?? []);
        setTotalPages(Math.max(1, response.data.data.pagination?.totalPages ?? 1));
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to load GIFs",
          severity: "error",
        });
      } finally {
        setIsGridLoading(false);
      }
    },
    [page, searchQuery, selectedTags]
  );

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get<InternalGifTagsResponse>(
        `${baseUrl}/internal-gifs/tags`
      );
      const tags = (response.data.data.tags ?? [])
        .map((tag) => tag.trim())
        .filter(Boolean);
      setAllTags(tags);
      writeTagCache(tags);
    } catch {
      // Tags are non-blocking; keep existing cached/empty state
    }
  }, []);

  useEffect(() => {
    void fetchGifs(page);
  }, [fetchGifs, page]);

  useEffect(() => {
    const cached = readTagCache();
    if (cached?.tags?.length) {
      setAllTags(cached.tags);
    }

    const isStale = !cached || Date.now() - cached.savedAt > TAG_CACHE_TTL_MS;
    if (isStale || !cached?.tags?.length) {
      void fetchTags();
    }
  }, [fetchTags]);

  const { upload, isPending, reset } = useUploadFiles({
    route: "internalGifs",
    api: uploadApiUrl,
    onUploadComplete: ({ files }) => {
      const key = files[0]?.objectInfo?.key;
      if (!key) {
        setSnackbar({
          open: true,
          message: "Upload finished but the server response had no object key.",
          severity: "warning",
        });
        return;
      }
      setUploadedObjectKey(key);
      setSnackbar({
        open: true,
        message: "Upload complete — ready to create GIF entry.",
        severity: "success",
      });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: err.message ?? "Upload failed",
        severity: "error",
      });
    },
  });

  const handleTagToggle = (tag: string) => {
    setPage(1);
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // Tags not yet selected, filtered by tag search query
  const tagSuggestions = useMemo(() => {
    const available = allTags.filter((tag) => !selectedTags.includes(tag));
    if (!tagSearch.trim()) return available.slice(0, 10);
    const filtered = available.filter((tag) =>
      tag.toLowerCase().includes(tagSearch.toLowerCase())
    );
    return filtered.slice(0, 10);
  }, [allTags, tagSearch, selectedTags]);

  const hasNoTagResults = tagSearch.trim().length > 0 && tagSuggestions.length === 0;

  const resetCreateForm = () => {
    reset();
    setUploadedObjectKey("");
    setNewGifName("");
    setNewGifTags("");
  };

  const handleAddGif = async () => {
    if (!uploadedObjectKey.trim() || !newGifName.trim() || !newGifTags.trim()) {
      setSnackbar({
        open: true,
        message: "Upload a GIF, then provide Name and Tags",
        severity: "warning",
      });
      return;
    }

    const tags = newGifTags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    if (!tags.length) {
      setSnackbar({
        open: true,
        message: "Provide at least one valid tag",
        severity: "warning",
      });
      return;
    }

    try {
      await axios.post(`${baseUrl}/internal-gifs`, {
        objectKey: uploadedObjectKey,
        title: newGifName.trim(),
        tags,
      });

      setSnackbar({
        open: true,
        message: "GIF added successfully",
        severity: "success",
      });
      setAllTags((prev) => {
        const merged = Array.from(new Set([...prev, ...tags])).sort((a, b) =>
          a.localeCompare(b)
        );
        writeTagCache(merged);
        return merged;
      });
      setDrawerOpen(false);
      resetCreateForm();
      if (page !== 1) {
        setPage(1);
      } else {
        await fetchGifs(1);
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to add GIF";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  return (
    <Stack spacing={2} sx={{ padding: 2 }}>
      {/* Header row: title, search, filter, add button — all in one line */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          padding: "10px 16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
          GIFs
        </Typography>

        {/* Search */}
        <TextField
          placeholder="Search GIFs..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          sx={{
            flex: 1,
            maxWidth: 360,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
              "&:hover": { backgroundColor: "#ebebeb" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#999", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Tag filter */}
        <ClickAwayListener onClickAway={() => setTagDropdownOpen(false)}>
          <Box sx={{ position: "relative" }}>
            <TextField
              placeholder="Filter by tags..."
              variant="outlined"
              size="small"
              value={tagSearch}
              onChange={(e) => {
                setTagSearch(e.target.value);
                setTagDropdownOpen(true);
              }}
              onFocus={() => setTagDropdownOpen(true)}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f5f5f5",
                  "&:hover": { backgroundColor: "#ebebeb" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon sx={{ color: "#999", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            {tagDropdownOpen && (
              <Paper
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: "auto",
                  borderRadius: 2,
                }}
                elevation={4}
              >
                {hasNoTagResults ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tags match your search
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {tagSuggestions.map((tag) => (
                      <ListItemButton
                        key={tag}
                        onClick={() => {
                          handleTagToggle(tag);
                          setTagSearch("");
                          setTagDropdownOpen(false);
                        }}
                      >
                        <ListItemText primary={tag} />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </Box>
        </ClickAwayListener>

        {/* Selected tag count badge */}
        {selectedTags.length > 0 && (
          <Chip
            label={`${selectedTags.length} tag${selectedTags.length > 1 ? "s" : ""}`}
            size="small"
            color="primary"
            onDelete={() => setSelectedTags([])}
          />
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Add button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetCreateForm();
            setDrawerOpen(true);
          }}
          sx={{
            backgroundColor: "#485CF3",
            "&:hover": { backgroundColor: "#3a4cc7" },
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 500,
            px: 2.5,
          }}
        >
          Add GIF
        </Button>
      </Box>

      {/* Active tag filters */}
      {selectedTags.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {selectedTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleTagToggle(tag)}
            />
          ))}
        </Box>
      )}

      {/* GIF Grid */}
      {isGridLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading GIFs...
        </Typography>
      ) : gifs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No GIFs found matching your search.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
          }}
        >
          {gifs.map((gif) => (
            <Box
              key={gif.id}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
                transition: "box-shadow 0.2s ease",
              }}
            >
              <img
                src={gif.url}
                alt={gif.title}
                style={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box sx={{ padding: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {gif.title}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {gif.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Page {page} of {Math.max(1, totalPages)}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={isGridLoading || page <= 1}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={isGridLoading || page >= totalPages}
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        >
          Next
        </Button>
      </Box>

      {/* Add GIF Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          resetCreateForm();
        }}
      >
        <Box sx={{ width: 400, padding: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6">Add New GIF</Typography>
            <IconButton
              onClick={() => {
                setDrawerOpen(false);
                resetCreateForm();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            <Box>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/gif,image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const list = e.target.files;
                  if (list?.length) {
                    setUploadedObjectKey("");
                    upload(list);
                  }
                  e.target.value = "";
                }}
              />
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CloudUploadIcon />}
                disabled={isPending}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none", py: 1.25 }}
              >
                Upload image to cloud
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Better Upload endpoint: <code style={{ wordBreak: "break-all" }}>{uploadApiUrl}</code>
                {" "}(route <strong>internalGifs</strong>).
              </Typography>
              {isPending ? <LinearProgress sx={{ mt: 2 }} /> : null}
            </Box>

            <Typography variant="body2" color={uploadedObjectKey ? "success.main" : "text.secondary"}>
              {uploadedObjectKey
                ? `Uploaded object key: ${uploadedObjectKey}`
                : "No file uploaded yet"}
            </Typography>

            <TextField
              label="Name"
              placeholder="Enter GIF name"
              fullWidth
              required
              value={newGifName}
              onChange={(e) => setNewGifName(e.target.value)}
              disabled={isPending}
            />

            <TextField
              label="Tags"
              placeholder="e.g. reaction, funny, happy"
              fullWidth
              required
              helperText="Comma-separated tags"
              value={newGifTags}
              onChange={(e) => setNewGifTags(e.target.value)}
              disabled={isPending}
            />

            <Button
              variant="contained"
              fullWidth
              disabled={isPending}
              onClick={handleAddGif}
              sx={{
                backgroundColor: "#485CF3",
                "&:hover": { backgroundColor: "#3a4cc7" },
                textTransform: "none",
                py: 1.5,
              }}
            >
              Add GIF
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
